import styled from '@emotion/styled'
import { tooltipClasses } from '@mui/material'
import {
  colors,
  typography,
  palette,
  Tooltip,
  Text,
  Button,
} from '@resiliatech/birdsong'

export const GoalsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 24px 60px 24px;
`

export const Header = styled(Text)`
  ${typography.up1.regularSans}
  font-family: 'Helvetica Neue';
`

export const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const GoalsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const GoalCardContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isInteractive',
})<{ isInteractive?: boolean }>`
  cursor: ${({ isInteractive }) => (isInteractive ? 'pointer' : 'default')};
  display: flex;
  background-color: ${colors.background.secondaryBackground};
  border-radius: 8px;
  gap: 16px;
  justify-content: space-between;
  position: relative;
  width: 100%;
  border: 1px solid ${colors.interactive.borderEnabledHoverGray};
  font-family: 'Helvetica Neue';
`

export const Details = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 0;
  justify-content: space-between;
  width: 100%;
`

export const TopSection = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  padding: 20px 24px 10px 24px;
  height: 170px;
`

export const Categories = styled.div`
  ${typography.down1.regularSans}
  font-family: 'Helvetica Neue';

  color: #003f7e;
  text-transform: uppercase;
  letter-spacing: 1px;

  display: flex;
  flex-flow: row;
  text-wrap: nowrap;
`

export const Category = styled.span`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;

  & + &:before {
    content: '•';
    margin: 0 8px;
  }
`

export const CategoryAndStatus = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`

export const RightStatus = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  & div {
    margin-right: 8px;
  }
`

export const GoalTitle = styled(Text)`
  ${typography.up1.regularSerif}
  color: ${colors.copy.headerDark};
  text-align: left;
  margin-right: 8px;
  flex-grow: 1;
  max-height: 60px; /* Limits to 2 lines (34px line-height × 2) */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Limit to 2 lines */
  -webkit-box-orient: vertical;
  white-space: normal; /* Allow wrapping */

  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px; /* 155.556% */
  font-family: 'Helvetica Neue';
`

export const GoalDescription = styled(Text)`
  ${typography.baseline.regularSans}
  font-family: 'Helvetica Neue';
  color: ${palette.text.secondary};
  text-align: left;
  word-break: break-all;
`

export const ChipContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 0;
  justify-content: space-between;
  width: 100%;
  padding: 0;
`

export const LeftChipsContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  width: calc(100% - 100px);
`

export const RightChipsContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`

const taskStatusColors = {
  completed: colors.feedback.successLight,
  unassigned: colors.feedback.errorLight,
  informational: colors.feedback.infoLight,
  shared: colors.warning.light,
}

export const Chip = styled.div<{
  variant?: 'completed' | 'unassigned' | 'informational' | 'shared'
}>`
  align-items: center;
  background-color: ${({ variant }) => variant && taskStatusColors[variant]};
  border: ${({ variant }) =>
    variant
      ? `1px solid ${taskStatusColors[variant]}`
      : `1px solid ${colors.interactive.disabledLight}`};
  border-radius: 4px;
  color: ${colors.copy.paragraphDark};
  display: flex;
  gap: 6px;
  padding: 6px;
  font-family: 'Helvetica Neue';
`

export const OwnerAndDates = styled.div`
  ${typography.down2.regularSans}
  font-family: 'Helvetica Neue';
  color: #003f7e !important;

  align-items: center;
  color: ${palette.text.secondary};
  display: flex;
  gap: 8px;
  margin-top: 15px;
`
export const Owner = styled.div`
  ${typography.down2.regularSans}
  font-family: 'Helvetica Neue';

  align-items: center;
  display: flex;
  gap: 5px;
  margin-right: 20px;
`

export const DateInfo = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`

export const Date = styled(Text)`
  ${typography.down2.regularSans}
  font-family: 'Helvetica Neue';
`

export const GoalProgressContainer = styled.div`
  display: inline-flex;
  position: relative;
`
export const GoalProgressLabelContainer = styled.div`
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`

export const EditOptions = styled.div`
  display: flex;
  align-items: center;
`

export const CompleteButton = styled(Button)`
  align-self: center;
`

export const AvatarInformation = styled.div`
  align-self: flex-end;
  display: flex;
  height: min-content;
`

export const AvatarTooltip = styled(Tooltip)`
  & .${tooltipClasses.tooltip} {
    background-color: ${colors.background.secondaryBackground};
    color: ${colors.common.white};
    font-size: 14px;
  }

  & .${tooltipClasses.arrow} {
    color: rgba(0, 0, 0, 0.8);
  }
`
export const FunderNameText = styled.span`
  font-family: 'Helvetica Neue';
  font-weight: 700;
  margin-left: -3px;
`
